import * as E from 'fp-ts/Either';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { LicenseStatEntry, licenseStats } from '../api/logs';
import { ChartData } from 'chart.js';
import { fillTimeSeries } from '../utils/charts';

export const TIME_UNITS = [
  'second',
  'minute',
  'hour',
  'day',
  'month',
  'year',
] as const;

export type TimeUnit = (typeof TIME_UNITS)[number];

export const useLicenseStats = (
  granularity: TimeUnit,
  from: Date | null,
  to: Date | null,
  app: string | null,
  forceRefresh: number // this one is used just to re-trigger the useEffect
) => {
  const auth = useAuth();
  const [data, setData] = useState<{
    logs: LicenseStatEntry[];
    chartData: ChartData<'line', {}>;
    apps: string[];
  } | null>(null);

  const [error, setError] = useState<string | null>(null);

  const dateRange = useMemo(() => {
    return from && to
      ? { from: from.toISOString(), to: to.toISOString() }
      : null;
  }, [from, to]);

  useEffect(() => {
    if (!auth?.token) {
      return;
    }
    licenseStats(auth.token, granularity, dateRange, app).then(
      (result): void => {
        if (E.isLeft(result)) {
          setError(
            result.left.helpMessage ??
              result.left.message ??
              'Failed to fetch logs'
          );
          return;
        }

        const sets = result.right.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.app]: [
              ...(acc[curr.app] ?? []),
              { x: curr.date, y: curr.hits },
            ],
          }),
          {} as Record<string, { x: string; y: number }[]>
        );

        const apps = Object.keys(sets);

        const datasets = Object.entries(sets).map(([label, data]) => ({
          label,
          data: fillTimeSeries(data, granularity),
        }));

        setData({ logs: result.right, chartData: { datasets }, apps });
      }
    );
  }, [auth?.token, granularity, dateRange, app, forceRefresh]);

  // Logs is raw from the API, chartData is formatted for chart.js
  return { ...data, error };
};
